import { useMemo } from 'react'
import { Box, Container, Grid, Stack, Typography } from '@mui/material'
import Link from 'next/link'

import Form from '@/components/base/form'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'

import { WPCF7FormField, WPPageData } from '@/types'

export type Contact = {
  type: string
  name: string
  email: string
  phone: string
}

export type ContactsProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      pretitile: string
      title: string
      text: string
      items: Contact[]
      form: {
        title: string
        text: string
        form: {
          fields: WPCF7FormField[]
          button: {
            text: string
          }
        }
      }
    }
  }
}

const Contacts = ({ pageProps }: ContactsProps) => {
  const { strings } = useAppStore()
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const form = useMemo(() => acf?.form.form, [acf])

  return (
    <>
      <Container>
        <Grid container alignItems="flex-start">
          <Grid size={{ xs: 4, md: 5 }} sx={{ display: 'grid', gap: 6 }}>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {acf?.pretitile ? (
                <Typography variant="overline" color="text.secondaryContrastText">
                  {acf.pretitile}
                </Typography>
              ) : null}

              {acf?.title ? (
                <Typography variant="h4" color="text.secondaryContrastText">
                  {acf.title}
                </Typography>
              ) : null}

              {acf?.text ? (
                <Typography variant="textLarge" color="text.secondaryContrastText">
                  {acf.text}
                </Typography>
              ) : null}
            </Box>

            <Stack component="ul" gap={2} sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {acf?.items.map(({ type, name, email, phone }, index) => (
                <Box component="li" key={`item-${index}`}>
                  {type ? (
                    <Typography
                      variant="textRegular"
                      sx={{ fontWeight: 'bold' }}
                      color="text.primary"
                    >
                      {type}
                    </Typography>
                  ) : null}

                  {name ? (
                    <Typography
                      variant="textRegular"
                      color="text.primary"
                    >{`${strings?.contacts.ref}: ${name}`}</Typography>
                  ) : null}

                  {email ? (
                    <Box>
                      <Typography
                        component="span"
                        variant="textRegular"
                        color="text.primary"
                      >{`${strings?.contacts.email}: `}</Typography>
                      <Box
                        component={Link}
                        href={`mailto:${email}`}
                        target="_blank"
                        color="text.primary"
                        scroll={false}
                        sx={{ textDecoration: 'none' }}
                      >
                        <Typography component="span" variant="textRegular">{`${email}`}</Typography>
                      </Box>
                    </Box>
                  ) : null}

                  {phone ? (
                    <Box>
                      <Typography
                        component="span"
                        variant="textRegular"
                        color="text.primary"
                      >{`${strings?.contacts.phone}: `}</Typography>
                      <Box
                        component={Link}
                        href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                        target="_blank"
                        color="text.primary"
                        scroll={false}
                        sx={{ textDecoration: 'none' }}
                      >
                        <Typography component="span" variant="textRegular">{`${phone}`}</Typography>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/**
           * form
           */}
          <Grid
            size={{ xs: 4, md: 6 }}
            offset={{ md: 1 }}
            sx={{
              display: 'grid',
              gap: 4,
              padding: { xs: 2, md: 4 },
              borderRadius: (theme) => theme.shape.borderRadius4,
              boxShadow: (theme) => theme.shadows[2]
            }}
          >
            <Box sx={{ display: 'grid', gap: 2 }}>
              {acf?.form?.title ? (
                <Typography variant="h4" color="text.secondaryContrastText">
                  {acf.form.title}
                </Typography>
              ) : null}

              {acf?.form?.text ? (
                <Typography variant="textLarge" color="text.secondaryContrastText">
                  {acf.form.text}
                </Typography>
              ) : null}
            </Box>

            <Form
              form_id={form?.fields[0].formId.toString() || ''}
              fields={form?.fields || []}
              submit={{
                text: form?.button.text || strings?.labels?.submit || '',
                color: 'primary'
              }}
              useGravity={true}
            />
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

export default Contacts
